import type { ClocktowerPlazaChallengeConfig } from './ClocktowerPlazaConfig.js';

export interface ClocktowerPlazaSerializedState {
  readonly version: 1;
  readonly selectedHour: number;
  readonly selectedMinute: number;
  readonly hintStage: number;
  readonly attempts: number;
  readonly complete: boolean;
}

export interface ClocktowerPlazaSnapshot extends ClocktowerPlazaSerializedState {
  readonly hourAngle: number;
  readonly minuteAngle: number;
  readonly hint?: string;
}

export class ClocktowerPlazaState {
  private selectedHour: number;
  private selectedMinute: number;
  private hintStage = 0;
  private attempts = 0;
  private completed = false;

  constructor(
    readonly config: ClocktowerPlazaChallengeConfig,
    saved?: Partial<ClocktowerPlazaSerializedState>,
  ) {
    this.selectedHour = config.startingHour;
    this.selectedMinute = config.startingMinute;
    if (saved?.version === 1) this.restore(saved);
  }

  advanceHour(delta = 1) {
    if (this.completed || !Number.isInteger(delta)) return false;
    this.selectedHour = ((this.selectedHour - 1 + delta) % 12 + 12) % 12 + 1;
    return true;
  }

  advanceMinute(delta = 5) {
    if (this.completed || !Number.isInteger(delta) || delta % 5 !== 0) return false;
    this.selectedMinute = ((this.selectedMinute + delta) % 60 + 60) % 60;
    return true;
  }

  check() {
    if (this.completed) return 'complete' as const;
    this.attempts += 1;
    if (this.selectedHour !== this.config.targetHour || this.selectedMinute !== this.config.targetMinute) {
      return 'incorrect' as const;
    }
    this.completed = true;
    return 'complete' as const;
  }

  requestHint() {
    this.hintStage = Math.min(this.config.hints.length, this.hintStage + 1);
    return this.snapshot.hint;
  }

  serialize(): ClocktowerPlazaSerializedState {
    return {
      version: 1,
      selectedHour: this.selectedHour,
      selectedMinute: this.selectedMinute,
      hintStage: this.hintStage,
      attempts: this.attempts,
      complete: this.completed,
    };
  }

  get snapshot(): ClocktowerPlazaSnapshot {
    return {
      ...this.serialize(),
      hourAngle: (this.selectedHour % 12) * 30 + this.selectedMinute * 0.5,
      minuteAngle: this.selectedMinute * 6,
      hint: this.hintStage > 0 ? this.config.hints[this.hintStage - 1] : undefined,
    };
  }

  private restore(saved: Partial<ClocktowerPlazaSerializedState>) {
    if (!Number.isInteger(saved.selectedHour) || Number(saved.selectedHour) < 1 || Number(saved.selectedHour) > 12) return;
    if (!Number.isInteger(saved.selectedMinute) || Number(saved.selectedMinute) < 0
      || Number(saved.selectedMinute) > 55 || Number(saved.selectedMinute) % 5 !== 0) return;
    this.selectedHour = Number(saved.selectedHour);
    this.selectedMinute = Number(saved.selectedMinute);
    this.hintStage = Math.min(this.config.hints.length, Math.max(0, Number(saved.hintStage) || 0));
    this.attempts = Math.max(0, Number(saved.attempts) || 0);
    this.completed = saved.complete === true
      && this.selectedHour === this.config.targetHour
      && this.selectedMinute === this.config.targetMinute;
  }
}
